// Inbox Composites — saved filter definitions (ADR 0029).
//
// Thin adapter over useUserScopedCrud + Zod-validated definition payload.
// The `definition` column ships between client + Postgres as JSON; on the
// way in we parse through `compositeDefinitionSchema` so a malformed row
// (from a future client version, hand-edit, or bug) falls back to the
// empty definition instead of crashing the sidebar.

import { useMemo } from 'react'
import type { QueryKey } from '@tanstack/react-query'
import type { Row } from '../lib/db-types'
import { useUserScopedCrud } from './useUserScopedCrud'
import {
  type CompositeDefinition,
  parseCompositeDefinition,
  EMPTY_DEFINITION,
} from '../lib/compositeSchema'

/** Row shape as it leaves the wire. `definition` is still Json at this
 * layer — callers get the parsed object via the `Composite` view. */
type RawComposite = Pick<
  Row<'inbox_composites'>,
  'id' | 'name' | 'icon' | 'color' | 'definition' | 'sort_order'
>

/** User-facing shape. `definition` is parsed so render code never has to
 * think about raw JSON. */
export interface Composite {
  id: string
  name: string
  icon: string
  color: string
  sort_order: number
  definition: CompositeDefinition
}

export const compositesKey = (userId: string | undefined): QueryKey =>
  ['inbox_composites', userId ?? 'anon']

type CompositeAddInput = {
  name: string
  icon?: string
  color?: string
  definition?: CompositeDefinition
}
type CompositeUpdate = Partial<{
  name: string
  icon: string
  color: string
  sort_order: number
  definition: CompositeDefinition
}>

export function useComposites() {
  const crud = useUserScopedCrud<RawComposite, CompositeAddInput, CompositeUpdate>({
    table: 'inbox_composites',
    select: 'id, name, icon, color, definition, sort_order',
    queryKey: compositesKey,
    rowForInsert: ({ name, icon, color, definition }, userId, nextSortOrder) => ({
      user_id: userId,
      name,
      icon: icon ?? '',
      color: color ?? 'blue',
      // Store the definition as plain JSON — Postgres accepts a JS object
      // via pg_net's jsonb coercion. Empty default keeps the filter safe
      // even if the UI didn't populate predicates yet.
      definition: (definition ?? EMPTY_DEFINITION) as unknown as Row<'inbox_composites'>['definition'],
      sort_order: nextSortOrder,
    }),
    rowForUpdate: (updates) => {
      // Serialise `definition` to plain JSON only if supplied; partial
      // updates on other fields shouldn't nuke the stored definition.
      if ('definition' in updates && updates.definition) {
        const { definition, ...rest } = updates
        return {
          ...rest,
          definition: definition as unknown as Row<'inbox_composites'>['definition'],
        }
      }
      return { ...updates }
    },
    optimisticRow: ({ name, icon, color, definition }, tempId, nextSortOrder) => ({
      id: tempId,
      name,
      icon: icon ?? '',
      color: color ?? 'blue',
      definition: (definition ?? EMPTY_DEFINITION) as unknown as Row<'inbox_composites'>['definition'],
      sort_order: nextSortOrder,
    }),
  })

  // Parsed-view of the list. Memoised so identity stays stable across
  // renders — downstream consumers that depend on `composites` in a
  // useEffect / useMemo deps list won't re-run on every render.
  const composites = useMemo<Composite[]>(
    () =>
      crud.items.map((r) => ({
        id: r.id,
        name: r.name,
        icon: r.icon,
        color: r.color,
        sort_order: r.sort_order,
        definition: parseCompositeDefinition(r.definition),
      })),
    [crud.items],
  )

  // Parse the raw row to a `Composite` so the return contract matches the
  // user-facing shape. Callers like OrganizeModal already expect the
  // parsed `Composite | null | undefined`; leaving this as `RawComposite`
  // broke the prop typing in Shell.tsx.
  const addComposite = async (input: CompositeAddInput): Promise<Composite | null | undefined> => {
    const raw = await crud.add(input)
    if (!raw) return null
    return {
      id: raw.id,
      name: raw.name,
      icon: raw.icon,
      color: raw.color,
      sort_order: raw.sort_order,
      definition: parseCompositeDefinition(raw.definition),
    }
  }

  return {
    composites,
    loading: crud.loading,
    addComposite,
    updateComposite: (id: string, updates: CompositeUpdate) => crud.update(id, updates),
    deleteComposite: (id: string) => crud.remove(id),
  }
}
